// Staff records with licence expiry (docs/plans/phase2-staff-records.md §3). URLs carry
// internal IDs only; departments and reasons travel in bodies.
import { queryOptions } from '@tanstack/react-query';
import {
  licenceNoticeListSchema,
  staffDocumentSchema,
  staffListSchema,
  staffRecordSchema,
  type SaveStaffEmploymentRequest,
  type UploadStaffDocumentRequest,
} from '@wodi/shared';

import { api } from './client.ts';
import { fetchDocumentBytes } from './clinical-documents.ts';

export const staffKeys = {
  all: ['staff'] as const,
  list: ['staff', 'list'] as const,
  licences: ['staff', 'licences'] as const,
  notices: ['staff', 'notices'] as const,
  record: (userId: string) => ['staff', 'record', userId] as const,
};

export const staffListQuery = queryOptions({
  queryKey: staffKeys.list,
  queryFn: () => api('GET', '/staff', { schema: staffListSchema }),
});

export const licencesToRenewQuery = queryOptions({
  queryKey: staffKeys.licences,
  queryFn: () => api('GET', '/staff/licences', { schema: staffListSchema }),
});

/** Home's licence notices; refreshed every five minutes while Home is open. */
export const licenceNoticesQuery = queryOptions({
  queryKey: staffKeys.notices,
  queryFn: () => api('GET', '/staff/licence-notices', { schema: licenceNoticeListSchema }),
  refetchInterval: 300_000,
});

export function staffRecordQuery(userId: string) {
  return queryOptions({
    queryKey: staffKeys.record(userId),
    queryFn: () => api('GET', `/staff/${userId}`, { schema: staffRecordSchema }),
  });
}

export function saveStaffEmployment(userId: string, body: SaveStaffEmploymentRequest) {
  return api('PUT', `/staff/${userId}/employment`, { body, schema: staffRecordSchema });
}

export function uploadStaffDocument(userId: string, body: UploadStaffDocumentRequest) {
  return api('POST', `/staff/${userId}/documents`, { body, schema: staffDocumentSchema });
}

export function staffDocumentEnteredInError(documentId: string, reason: string) {
  return api('POST', `/staff/documents/${documentId}/entered-in-error`, {
    body: { reason },
    schema: staffDocumentSchema,
  });
}

export function markLicenceNoticeSeen(noticeId: string) {
  return api('POST', `/staff/licence-notices/${noticeId}/seen`);
}

/** A licence copy's bytes: audited as a read when viewing, as an export when downloading. */
export function fetchStaffDocumentContent(
  documentId: string,
  mode: 'view' | 'download',
): Promise<Blob> {
  return fetchDocumentBytes(`/api/v1/staff/documents/${documentId}/content?mode=${mode}`);
}
